/* eslint-disable react-hooks/exhaustive-deps */
//#region Libraries
import { useEffect, useState } from "react";
//#endregion
//#region Styles
import { Container } from "./styles";
import { SkelletonLoader } from "components/CustomTableList/components/TableListItem/styles";
//#endregion
//#region Components
import ListHeader from "./components/ListHeader/ListHeader";
import CustomTabs from "components/CustomTabs/CustomTabs";
import CustomTableList from "components/CustomTableList/CustomTableList";
import Preloader from "components/Preloader/Preloader";
import Paginator from "components/Paginator/Paginator";
import ListItem from "./components/ListItem/ListItem";
import NotificationNoContent from "components/NotificationNoContent/NotificationNoContent";
import InSyncOfEventUnprocessedButton from "./components/InSyncOfEventUnprocessedButton/InSyncOfEventUnprocessedButton";
import SyncEventUnprocessedDialog from "./components/SyncEventUnprocessedDialog/SyncEventUnprocessedDialog";
import DialogNotificationLeaveViewInSync from "views/components/DialogNotifyLeaveView/DialogNotifyLeaveView";
//#endregion
//#region Utils
import useMainContext from "utils/context/useMainContext";
import useFilters from "./components/Filters/utils/hooks/useFilters";
import useCurrentTabOfEventList from "./utils/hooks/useCurrentTabOfEventList";
import useEventList from "./utils/hooks/useEventList";
import useSyncOfEventUnprocessed from "./utils/hooks/useSyncOfEventUnprocessed";
import useTabData from "./utils/hooks/useTabData";
import useTitleTabViews from "./utils/hooks/useTitleTabViews";
import { getViewTabTitles } from "./utils/helpers";
import { HEADERS_TABLE_LIST, RECORDS_BY_PAGE } from "./utils/constants";
//#endregion

const ListComponent = ({ isTeacherUser, setToastMessage, hasReportWaitlist }) => {
	const mainContext = useMainContext();
	//#region States
	const [dataPaginator, setDataPaginator] = useState({
		currentPage: 1,
		quantityPages: 0,
	});
	const [canShowDialogLeavingView, setCanShowDialogLeavingView] = useState(false);
	//#endregion
	//#region Custom hooks
	const tabData = useTabData({ isTeacherUser });
	const currentTab = useCurrentTabOfEventList({
		isTeacherUser,
		tabDataValues: tabData.values
	});
	const filters = useFilters();
	const titleTabViews = useTitleTabViews({
		dataPaginator, setDataPaginator,
		currentTab, filters, tabData
	});
	const eventList = useEventList({
		currentTabIdx: currentTab.index,
		dataPaginator, fillTitleViews: titleTabViews.fill,
		filters, tabData,
		isTeacherUser, setToastMessage,
	});
	const syncOfEventUnprocessed = useSyncOfEventUnprocessed({
		setCanShowDialogLeavingView
	});
	//#endregion
	//#region Effects
	useEffect(() => {
		mainContext.reloadForUnexpectedError(eventList.error);
	}, [mainContext.showDialogUnexpectedError]);
	useEffect(() => {
		if (currentTab.index < 0) return;
		setDataPaginator(prev => ({
			...prev,
			currentPage: 1,
		}));
		eventList.fill();
	}, [currentTab.index]);
	//#endregion
	//#region Functions
	const changeDataPaginator = value => {
		setDataPaginator(prev => ({
			...prev,
			currentPage: value,
		}));
		eventList.fill();
	};
	const canShowPaginator = () => {
		if (!titleTabViews.value || !currentTab.isSomeTab()) return false;
		const quantity = titleTabViews.value[tabData.fields[currentTab.index]];
		return quantity > RECORDS_BY_PAGE;
	};
	const getHeaders = () => {
		if (isTeacherUser) return HEADERS_TABLE_LIST.TEACHER;
		return currentTab.claims.isUnprocessed
			? [
				...HEADERS_TABLE_LIST.ADMIN_WITH_STATE,
				{
					control: (
						<InSyncOfEventUnprocessedButton
							noRecords={eventList.value.length === 0}
							toSynchronizeFromTheListingRequest={
								eventList.toSynchronize
							}
							{...syncOfEventUnprocessed}
						/>
					),
				},
			]
			: HEADERS_TABLE_LIST.ADMIN;
	};
	//#endregion
	if (eventList.error) return null;
	const showNotificationNoContent =
		eventList.isLoading !== null &&
		!eventList.isLoading &&
		eventList.value.length === 0;
	const headers = getHeaders();
	return (
		<Container>
			<ListHeader
				isTeacherUser={isTeacherUser}
				fillEventList={eventList.fill}
				resetCurrentPage={() =>
					setDataPaginator(prev => ({
						...prev,
						currentPage: 1,
					}))
				}
				filters={filters}
				currentTabIdx={currentTab.index}
				hasReportWaitlist={hasReportWaitlist}
			/>
			{!isTeacherUser && (
				<CustomTabs
					viewTabTitles={getViewTabTitles({
						quantities: titleTabViews.value,
						isTeacherUser,
					})}
					currentTabIdx={currentTab.index}
					changeCurrentTabIdx={idx => currentTab.setIndex(idx)}
					tabWidth={150}
				/>
			)}
			<CustomTableList headers={headers}>
				{!eventList.isLoading
					? eventList.value.map((item, idx) => {
						return (
							<ListItem
								key={idx}
								{...item}
								isTeacherUser={isTeacherUser}
								currentTab={currentTab}
								tabData={tabData}
								inSyncEventUnprocessed={syncOfEventUnprocessed.inSync}
							/>
						);
					})
					: Array(4)
						.fill(0)
						.map((_, idx) => (
							<SkelletonLoader key={idx}>
								{headers.map((_, idx2) => (
									<td key={idx2}>
										<div></div>
									</td>
								))}
							</SkelletonLoader>
						))}
			</CustomTableList>
			{showNotificationNoContent && (
				<NotificationNoContent
					title="No tienes eventos en esta fecha seleccionada"
					description={`Empieza creando más eventos seleccionando "Crear evento"`}
					variant="secondary"
					marginTop="60px"
				/>
			)}
			{canShowPaginator() && (
				<Paginator
					data={dataPaginator}
					changeData={changeDataPaginator}
					alignSelf="flex-end"
				/>
			)}
			{currentTab.claims.isUnprocessed && (
				<>
					<SyncEventUnprocessedDialog
						fillEventList={eventList.fill}
						syncOfEventUnprocessed={syncOfEventUnprocessed}
					/>
					<DialogNotificationLeaveViewInSync
						canShowDialogLeavingView={canShowDialogLeavingView}
						content={
							<p className="paragraph center">
								Recuerda que al salir, no se actualizarán correctamente los estados de los eventos <b>no procesados.</b>
							</p>}
					/>
				</>
			)}
			<Preloader hidden={!eventList.isLoading} />
		</Container>
	);
};

export default ListComponent;
